import React, { useState, useRef, useEffect, useCallback } from "react";
import { CloudUpload, X, Loader2, AlertCircle } from "lucide-react";

const API_BASE = import.meta.env.VITE_BACKEND_URL;

const RETURN_REASONS = [
  "Size issue",
  "Damaged product",
  "Wrong item received",
  "Other",
];

// Order ID must be at least 6 chars, alphanumeric/hyphens, no pure spaces or generic words
const GENERIC_ORDER_PATTERNS = [
  /^(test|order|id|sample|abc|123|dummy|na|n\/a|nil|none|xxx)$/i,
];
const ORDER_ID_REGEX = /^[a-zA-Z0-9#\-]{4,}$/;

const validateOrderId = (val) => {
  const trimmed = val.trim();
  if (!trimmed) return "Order ID is required";
  if (!ORDER_ID_REGEX.test(trimmed)) return "Enter a valid Order ID (letters, numbers, hyphens only — min 4 chars)";
  if (GENERIC_ORDER_PATTERNS.some((p) => p.test(trimmed))) return "Please enter your actual Order ID from the confirmation email";
  return null;
};

/* ── HAPTIC ── */
const haptic = (type = "light") => {
  if (!navigator.vibrate) return;
  const patterns = { light: [10], medium: [20], error: [30, 50, 30] };
  navigator.vibrate(patterns[type] || [10]);
};

/* ── TOAST ── */
let toastIdCounter = 0;
const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = "info") => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, addToast, removeToast };
};

/* ── TOAST CONTAINER ── */
const ToastContainer = ({ toasts, removeToast }) => (
  <div className="rt-toast-container">
    {toasts.map((t) => (
      <div key={t.id} className={`rt-toast rt-toast--${t.type}`}>
        <span className="rt-toast-msg">{t.message}</span>
        <button className="rt-toast-close" onClick={() => removeToast(t.id)}>
          <X size={11} />
        </button>
      </div>
    ))}
  </div>
);

export default function Return() {
  const { toasts, addToast, removeToast } = useToast();

  const [form, setForm] = useState({
    orderId: "",
    name: "",
    email: "",
    phone: "",
    reason: "",
    message: "",
    image: "",
  });

  const [reasonOpen, setReasonOpen] = useState(false);
  const reasonRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Track which step fields are "touched" for upload gate
  const requiredDetailsFilled = () => {
    return (
      form.orderId.trim().length >= 4 &&
      !validateOrderId(form.orderId) &&
      form.name.trim().length > 0 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
      /^[0-9]{10}$/.test(form.phone.replace(/[\s-]/g, "")) &&
      form.reason !== ""
    );
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (reasonRef.current && !reasonRef.current.contains(e.target)) {
        setReasonOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  /* ── VALIDATION ── */
  const validateForm = () => {
    const errors = {};
    const orderErr = validateOrderId(form.orderId);
    if (orderErr) errors.orderId = orderErr;
    if (!form.name.trim()) errors.name = "Name is required";
    if (!form.email.trim()) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = "Invalid email format";
    if (!form.phone.trim()) errors.phone = "Phone number is required";
    else if (!/^[0-9]{10}$/.test(form.phone.replace(/[\s-]/g, ""))) errors.phone = "Phone must be 10 digits";
    if (!form.reason) errors.reason = "Please select a return reason";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ── IMAGE UPLOAD (gated) ── */
  const handleUploadAttempt = (file) => {
    if (!requiredDetailsFilled()) {
      haptic("error");
      addToast("Please complete all required fields before uploading an image.", "warning");
      return;
    }
    uploadImage(file);
  };

  const uploadImage = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      haptic("error");
      addToast("Please upload a valid image file.", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      haptic("error");
      addToast("Image size must be less than 5MB.", "error");
      return;
    }
    setUploading(true);
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", "veshh_return_form");
    try {
      const res = await fetch("https://api.cloudinary.com/v1_1/dmrlotfsv/image/upload", { method: "POST", body: data });
      if (!res.ok) throw new Error("Upload failed");
      const json = await res.json();
      setForm((p) => ({ ...p, image: json.secure_url }));
      haptic("light");
      addToast("Image uploaded successfully.", "success");
    } catch (err) {
      haptic("error");
      addToast("Image upload failed. Please try again.", "error");
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  /* ── SUBMIT ── */
  const submitReturn = async () => {
    if (!validateForm()) {
      haptic("error");
      addToast("Please fix the errors before submitting.", "warning");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/returns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: form.orderId.trim(),
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          reason: form.reason,
          message: form.message.trim(),
          images: form.image ? [{ url: form.image }] : [],
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Return request failed");
      haptic("medium");
      setSubmitted(true);
    } catch (err) {
      haptic("error");
      addToast(err.message || "Failed to submit. Please try again.", "error");
      console.error("Submit error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── SUCCESS ── */
  if (submitted) {
    return (
      <>
        <style>{returnStyles}</style>
        <div className="rt-root rt-root--center">
          <div className="rt-success-box">
            <span className="rt-success-icon">✓</span>
            <h2 className="rt-success-title">Request Received</h2>
            <p className="rt-success-sub">
              We've received your return request. Our team will review it carefully and get back to you shortly.
            </p>
          </div>
        </div>
      </>
    );
  }

  const uploadLocked = !requiredDetailsFilled();

  /* ── FORM ── */
  return (
    <>
      <style>{returnStyles}</style>
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className="rt-root">

        {/* ── HEADER ── */}
        <div className="rt-header">
          <span className="rt-eyebrow">Customer Service</span>
          <h1 className="rt-title">Return <span className="rt-title-bold">Request</span></h1>
        </div>

        <div className="rt-rule-wrap"><hr className="rt-hr" /></div>

        {/* ── BODY ── */}
        <div className="rt-body">

          {/* LEFT: CONTEXT */}
          <div className="rt-context">
            <p className="rt-context-text">
              Fill out the form and we'll get back to you within 24 hours. Make sure to have your Order ID ready from your confirmation email.
            </p>
            <div className="rt-rule-wrap rt-rule-wrap--inner"><hr className="rt-hr" /></div>
            <div className="rt-context-block">
              <span className="rt-context-label">Return Window</span>
              <span className="rt-context-value">2–3 days from receipt</span>
            </div>
            <div className="rt-rule-wrap rt-rule-wrap--inner"><hr className="rt-hr" /></div>
            <div className="rt-context-block">
              <span className="rt-context-label">Refund Timeline</span>
              <span className="rt-context-value">3–4 business days</span>
            </div>
            <div className="rt-rule-wrap rt-rule-wrap--inner"><hr className="rt-hr" /></div>
            <div className="rt-context-block">
              <span className="rt-context-label">Support</span>
              <a href="mailto:support@veshheon.in" className="rt-context-link">support@veshheon.in</a>
            </div>
          </div>

          {/* RIGHT: FORM */}
          <div className="rt-form">

            {/* ── 01: ORDER DETAILS ── */}
            <div className="rt-step-label">01 · Order Details</div>

            <div className="rt-field">
              <label className="rt-label">Order ID <span className="rt-req">*</span></label>
              <input
                className={`rt-input ${fieldErrors.orderId ? 'rt-input--err' : ''}`}
                placeholder="From your confirmation email"
                value={form.orderId}
                onChange={(e) => {
                  setForm({ ...form, orderId: e.target.value });
                  setFieldErrors({ ...fieldErrors, orderId: '' });
                }}
                onBlur={() => {
                  const err = validateOrderId(form.orderId);
                  if (err) {
                    haptic("light");
                    setFieldErrors((prev) => ({ ...prev, orderId: err }));
                  }
                }}
              />
              {fieldErrors.orderId && <span className="rt-field-err">{fieldErrors.orderId}</span>}
            </div>

            {/* ── 02: YOUR DETAILS ── */}
            <div className="rt-step-label">02 · Your Details</div>

            <div className="rt-field">
              <label className="rt-label">Full Name <span className="rt-req">*</span></label>
              <input
                className={`rt-input ${fieldErrors.name ? 'rt-input--err' : ''}`}
                placeholder="Your name"
                value={form.name}
                onChange={(e) => { setForm({ ...form, name: e.target.value }); setFieldErrors({ ...fieldErrors, name: '' }); }}
              />
              {fieldErrors.name && <span className="rt-field-err">{fieldErrors.name}</span>}
            </div>

            <div className="rt-field">
              <label className="rt-label">Email <span className="rt-req">*</span></label>
              <input
                type="email"
                className={`rt-input ${fieldErrors.email ? 'rt-input--err' : ''}`}
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => { setForm({ ...form, email: e.target.value }); setFieldErrors({ ...fieldErrors, email: '' }); }}
              />
              {fieldErrors.email && <span className="rt-field-err">{fieldErrors.email}</span>}
            </div>

            <div className="rt-field">
              <label className="rt-label">Phone <span className="rt-req">*</span></label>
              <input
                className={`rt-input ${fieldErrors.phone ? 'rt-input--err' : ''}`}
                placeholder="10-digit mobile number"
                value={form.phone}
                onChange={(e) => { setForm({ ...form, phone: e.target.value }); setFieldErrors({ ...fieldErrors, phone: '' }); }}
              />
              {fieldErrors.phone && <span className="rt-field-err">{fieldErrors.phone}</span>}
            </div>

            {/* ── 03: RETURN REASON ── */}
            <div className="rt-step-label">03 · Return Reason</div>

            <div className="rt-field">
              <label className="rt-label">Reason <span className="rt-req">*</span></label>
              <div className="rt-dropdown-wrap" ref={reasonRef}>
                <button
                  type="button"
                  className={`rt-dropdown-trigger ${fieldErrors.reason ? 'rt-input--err' : ''} ${!form.reason ? 'rt-dropdown-placeholder' : ''}`}
                  onClick={() => setReasonOpen((o) => !o)}
                >
                  {form.reason || "Select a reason"}
                  <span className={`rt-dropdown-chevron ${reasonOpen ? 'rt-dropdown-chevron--open' : ''}`}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </button>
                {reasonOpen && (
                  <div className="rt-dropdown-menu">
                    {RETURN_REASONS.map((r) => (
                      <button
                        key={r}
                        type="button"
                        className={`rt-dropdown-option ${form.reason === r ? 'rt-dropdown-option--active' : ''}`}
                        onClick={() => {
                          setForm({ ...form, reason: r });
                          setFieldErrors({ ...fieldErrors, reason: '' });
                          setReasonOpen(false);
                          haptic("light");
                        }}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {fieldErrors.reason && <span className="rt-field-err">{fieldErrors.reason}</span>}
            </div>

            <div className="rt-field">
              <label className="rt-label">Additional Details <span className="rt-optional">(optional)</span></label>
              <textarea
                className="rt-input rt-textarea"
                placeholder="Describe the issue in more detail..."
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
            </div>

            {/* ── 04: ATTACH PHOTO ── */}
            <div className="rt-step-label">04 · Attach Photo</div>

            <div className="rt-field">
              <label className="rt-label">
                Image <span className="rt-optional">(optional · max 5MB)</span>
              </label>

              {!form.image ? (
                <label
                  className={`rt-upload-zone ${uploadLocked ? 'rt-upload-zone--locked' : ''}`}
                  onClick={(e) => {
                    if (uploadLocked) {
                      e.preventDefault();
                      haptic("error");
                      addToast("Complete all required fields above before uploading.", "warning");
                    }
                  }}
                >
                  {uploading ? (
                    <Loader2 className="rt-upload-spinner" size={20} />
                  ) : uploadLocked ? (
                    <>
                      <span className="rt-upload-lock-icon">⊘</span>
                      <span className="rt-upload-text">Fill required fields first</span>
                    </>
                  ) : (
                    <>
                      <CloudUpload size={20} className="rt-upload-icon" />
                      <span className="rt-upload-text">Click to upload</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    disabled={uploading || uploadLocked}
                    onChange={(e) => handleUploadAttempt(e.target.files[0])}
                  />
                </label>
              ) : (
                <div className="rt-upload-done">
                  <span className="rt-upload-done-text">✓ Image uploaded</span>
                  <button className="rt-upload-remove" onClick={() => {
                    setForm({ ...form, image: "" });
                    haptic("light");
                  }}>
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>

            {/* ── SUBMIT ── */}
            <button
              className="rt-submit"
              disabled={submitting || uploading}
              onClick={submitReturn}
            >
              {submitting ? (
                <span className="rt-submit-loading">
                  <Loader2 size={14} className="rt-spinner" />
                  Submitting...
                </span>
              ) : (
                "Submit Return Request"
              )}
            </button>

          </div>
        </div>

        <div className="rt-rule-wrap"><hr className="rt-hr" /></div>
        <div className="rt-closing">
          <span className="rt-closing-text">VESHH-EON · Returns · support@veshheon.in</span>
        </div>

      </div>
    </>
  );
}

const returnStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

  .rt-root {
    background: #f8f8f8;
    min-height: 100vh;
    font-family: 'Exo 2', sans-serif;
    padding-bottom: 80px;
  }
  .rt-root--center {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  /* ── TOAST ── */
  .rt-toast-container {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    pointer-events: none;
  }

  .rt-toast {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    font-family: 'Exo 2', sans-serif;
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    white-space: nowrap;
    pointer-events: all;
    animation: rt-toast-in 0.25s ease forwards;
    max-width: 90vw;
  }
  @keyframes rt-toast-in {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .rt-toast--info    { background: #000; color: #f8f8f8; }
  .rt-toast--success { background: #000; color: black; }
  .rt-toast--warning { background: #fff; color: #f8f8f8; border: 1px solid black; }
  .rt-toast--error   { background: #000; color: #f8f8f8; }

  .rt-toast-msg { flex: 1; }

  .rt-toast-close {
    background: transparent;
    border: none;
    cursor: pointer;
    color: inherit;
    opacity: 0.5;
    display: flex;
    align-items: center;
    padding: 0;
  }
  .rt-toast-close:hover { opacity: 1; }

  /* ── HEADER ── */
  .rt-header {
    max-width: 1200px;
    margin: 0 auto;
    padding: 64px 80px 40px;
  }

  .rt-eyebrow {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.5em;
    text-transform: uppercase;
    color: black;
    margin-bottom: 12px;
  }
  .rt-eyebrow::before {
    content: '';
    width: 24px;
    height: 1px;
    background: black;
    display: block;
    flex-shrink: 0;
  }

  .rt-title {
    font-size: clamp(32px, 4.5vw, 60px);
    font-weight: 200;
    color: #000;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    line-height: 1;
    margin: 0;
  }
  .rt-title-bold { font-weight: 600; }

  /* ── RULES ── */
  .rt-rule-wrap {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 80px;
  }
  .rt-rule-wrap--inner { padding: 0; }
  .rt-hr {
    border: none;
    border-top: 1px solid black;
    margin: 0;
  }

  /* ── BODY ── */
  .rt-body {
    max-width: 1200px;
    margin: 0 auto;
    padding: 64px 80px;
    display: grid;
    grid-template-columns: 1fr 1.6fr;
    gap: 80px;
    align-items: start;
  }

  /* ── CONTEXT ── */
  .rt-context { display: flex; flex-direction: column; gap: 0; }

  .rt-context-text {
    font-size: 13px;
    font-weight: 300;
    color: black;
    line-height: 2;
    letter-spacing: 0.04em;
    margin-bottom: 24px;
  }

  .rt-context-block {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 18px 0;
  }

  .rt-context-label {
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0.45em;
    text-transform: uppercase;
    color: black;
  }

  .rt-context-value {
    font-size: 13px;
    font-weight: 400;
    letter-spacing: 0.06em;
    color: #000;
  }

  .rt-context-link {
    font-size: 13px;
    font-weight: 400;
    letter-spacing: 0.06em;
    color: #000;
    text-decoration: none;
    position: relative;
    display: inline-block;
    padding-bottom: 2px;
  }
  .rt-context-link::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0;
    width: 100%;
    height: 1px;
    background: black;
    transition: background 0.2s ease;
  }
  .rt-context-link:hover::after { background: #000; }

  /* ── FORM ── */
  .rt-form { display: flex; flex-direction: column; gap: 24px; }

  .rt-step-label {
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0.45em;
    text-transform: uppercase;
    color: black;
    padding-top: 12px;
    border-top: 1px solid black;
    margin-top: 4px;
  }
  .rt-form > .rt-step-label:first-of-type {
    border-top: none;
    padding-top: 0;
    margin-top: 0;
  }

  .rt-field { display: flex; flex-direction: column; gap: 8px; }

  .rt-label {
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0.45em;
    text-transform: uppercase;
    color: black;
  }

  .rt-req { color: rgba(0,0,0,0.3); }
  .rt-optional { font-weight: 300; color: black; letter-spacing: 0.2em; }

  .rt-input {
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(0,0,0,0.15);
    outline: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 13px;
    font-weight: 300;
    letter-spacing: 0.06em;
    color: #000;
    padding: 10px 0;
    width: 100%;
    transition: border-color 0.2s ease;
    border-radius: 0;
    -webkit-appearance: none;
  }
  .rt-input::placeholder { color: black; }
  .rt-input:focus { border-bottom-color: #000; }
  .rt-input--err { border-bottom-color: black !important; }
  .rt-textarea { resize: none; line-height: 1.8; }

  .rt-field-err {
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: black;
  }

  /* ── DROPDOWN ── */
  .rt-dropdown-wrap { position: relative; }

  .rt-dropdown-trigger {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(0,0,0,0.15);
    outline: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 13px;
    font-weight: 300;
    letter-spacing: 0.06em;
    color: #000;
    padding: 10px 0;
    cursor: pointer;
    text-align: left;
    transition: border-color 0.2s ease;
    border-radius: 0;
  }
  .rt-dropdown-trigger:focus { border-bottom-color: #000; }
  .rt-dropdown-placeholder { color: black; }

  .rt-dropdown-chevron {
    display: flex;
    align-items: center;
    color: black;
    transition: transform 0.2s ease;
    flex-shrink: 0;
  }
  .rt-dropdown-chevron--open { transform: rotate(180deg); }

  .rt-dropdown-menu {
    position: absolute;
    top: calc(100% + 2px);
    left: 0; right: 0;
    background: #f8f8f8;
    border: 1px solid black;
    z-index: 50;
    animation: rt-fade-in 0.15s ease;
  }
  @keyframes rt-fade-in {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .rt-dropdown-option {
    display: block;
    width: 100%;
    padding: 11px 14px;
    font-family: 'Exo 2', sans-serif;
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: black;
    background: transparent;
    border: none;
    border-top: 1px solid black;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
  }
  .rt-dropdown-option:first-child { border-top: none; }
  .rt-dropdown-option:hover { background: #000; color: #fff; }
  .rt-dropdown-option--active { color: #000; font-weight: 600; }

  /* ── UPLOAD ── */
  .rt-upload-zone {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    border: 1px dashed rgba(0,0,0,0.15);
    padding: 28px;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease;
  }
  .rt-upload-zone:hover:not(.rt-upload-zone--locked) {
    border-color: black;
    background: #f8f8f8;
  }
  .rt-upload-zone--locked {
    cursor: not-allowed;
    opacity: 0.45;
    border-style: dashed;
  }

  .rt-upload-icon { color: black; }
  .rt-upload-lock-icon {
    font-size: 18px;
    color: black;
    line-height: 1;
  }
  .rt-upload-spinner { color: black; animation: rt-spin 1s linear infinite; }
  @keyframes rt-spin { to { transform: rotate(360deg); } }

  .rt-upload-text {
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: black;
  }

  .rt-upload-done {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid black;
    padding: 12px 14px;
  }

  .rt-upload-done-text {
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: black;
  }

  .rt-upload-remove {
    background: transparent;
    border: none;
    color: black;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: color 0.2s ease;
  }
  .rt-upload-remove:hover { color: #000; }

  /* ── SUBMIT ── */
  .rt-submit {
    width: 100%;
    background: #000;
    color: #f8f8f8;
    border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    padding: 16px;
    cursor: pointer;
    transition: background 0.2s ease;
    margin-top: 8px;
  }
  .rt-submit:hover:not(:disabled) { background: #222; }
  .rt-submit:disabled { background: black; cursor: not-allowed; }

  .rt-submit-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .rt-spinner { animation: rt-spin 1s linear infinite; }

  /* ── SUCCESS ── */
  .rt-success-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    max-width: 400px;
    text-align: center;
    padding: 60px 40px;
    border: 1px solid black;
  }

  .rt-success-icon {
    font-size: 22px;
    font-weight: 300;
    color: black;
    width: 56px;
    height: 56px;
    border: 1px solid black;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .rt-success-title {
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.35em;
    text-transform: uppercase;
    color: #000;
    margin: 0;
  }

  .rt-success-sub {
    font-size: 12px;
    font-weight: 300;
    letter-spacing: 0.08em;
    color: #f8f8f8;
    line-height: 2;
    margin: 0;
  }

  /* ── CLOSING ── */
  .rt-closing {
    max-width: 1200px;
    margin: 0 auto;
    padding: 24px 80px 0;
    text-align: center;
  }
  .rt-closing-text {
    font-size: 8px;
    font-weight: 300;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: black;
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .rt-header  { padding: 48px 40px 32px; }
    .rt-rule-wrap { padding: 0 40px; }
    .rt-body    { padding: 48px 40px; gap: 48px; }
    .rt-closing { padding: 20px 40px 0; }
  }

  @media (max-width: 768px) {
    .rt-header  { padding: 40px 20px 28px; }
    .rt-rule-wrap { padding: 0 20px; }
    .rt-body    { padding: 36px 20px; grid-template-columns: 1fr; gap: 40px; }
    .rt-closing { padding: 20px 20px 0; }
    .rt-toast-container { bottom: 16px; width: 90vw; }
    .rt-toast { white-space: normal; text-align: center; justify-content: center; }
  }

  @media (max-width: 360px) {
    .rt-header  { padding: 32px 14px 24px; }
    .rt-rule-wrap { padding: 0 14px; }
    .rt-body    { padding: 28px 14px; }
    .rt-closing { padding: 16px 14px 0; }
  }
`;
